import dayjs from 'dayjs';
import { mapDatabaseStatusToDisplay } from '../contractStatus';
import {
    summariseClientContracts,
    type NumericField,
    type SummaryContract,
} from './clientContractSummary';

/**
 * The client sheet ("Fisa client"): the printed page the Delphi program produces from the
 * Contracts screen. Its layout is read from `QuickRep1` in `Ucontracte.dfm`: the client's
 * name, number, address, ID card, locality and observations on top, one line per contract
 * (number, name, pledged object, quantity, daily rate, rate, amount, due date, expiry date,
 * status), and the five totals of the legacy `FisaClick` at the bottom.
 *
 * The sheet is built here once, as plain text cells, so the page on screen, the printout and
 * the CSV export can never show different figures.
 */

/** The client fields the sheet prints; everything is optional, as the client record has it. */
export interface ClientSheetClient {
    ClNr: number;
    Nume: string;
    Oras?: string | null;
    Str?: string | null;
    Nr?: string | null;
    Ap?: string | null;
    SerieCI?: string | null;
    NrCI?: string | null;
    Categorie?: string | null;
    Notite?: string | null;
    Observatii?: string | null;
}

/** A contract row as the contracts endpoint returns it. */
export interface ClientSheetContract extends SummaryContract {
    Nr: number;
    NrContract?: string | null;
    Nume_client?: string | null;
    obiect_amanetat?: string | null;
    quantity?: NumericField;
    Curs_BNR?: NumericField;
    Termen_restituire?: string | null;
    Data_Exp?: string | null;
}

export interface ClientSheetField {
    key: string;
    label: string;
    value: string;
}

export interface ClientSheetColumn {
    key: string;
    label: string;
    align: 'left' | 'right';
}

export interface ClientSheetRow {
    key: number;
    /** One text cell per column, in the order of `ClientSheet.columns`. */
    cells: string[];
}

export interface ClientSheet {
    /** The client's name, printed as the heading. */
    clientName: string;
    /** Client number, address, ID card, locality, category. */
    details: ClientSheetField[];
    observations: string;
    columns: ClientSheetColumn[];
    rows: ClientSheetRow[];
    totals: ClientSheetField[];
}

/** Looks a label up by its key under `contracts.clientSheet`; the page passes i18next's `t`. */
export type ClientSheetTranslate = (key: string) => string;

/** An empty value reads as a dash, never as a blank. */
const orDash = (value: string | number | null | undefined): string => {
    const text = String(value ?? '').trim();
    return text || '-';
};

const toNumber = (value: NumericField | undefined): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

/** Currency is shown without decimals, as everywhere else in the application. */
const formatRon = (amount: number): string => `${amount.toFixed(0)} RON`;

const formatDate = (value: string | null | undefined): string => {
    if (!value) {
        return '-';
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '-';
};

/** A rate that is absent or zero reads as a dash, as in the contracts grid. */
const formatRate = (value: NumericField | undefined): string => {
    const rate = toNumber(value);
    return rate ? rate.toFixed(4) : '-';
};

/** The street line of the sheet: "Str. <street> Nr. <number> Ap. <flat>", the parts present. */
export const formatSheetAddress = (client: ClientSheetClient): string =>
    [
        ['Str.', client.Str],
        ['Nr.', client.Nr],
        ['Ap.', client.Ap],
    ]
        .map(([prefix, value]) => [prefix, String(value ?? '').trim()])
        .filter(([, value]) => value !== '')
        .map(([prefix, value]) => `${prefix} ${value}`)
        .join(' ');

const CONTRACT_COLUMNS: ReadonlyArray<readonly [string, ClientSheetColumn['align']]> = [
    ['contractNumber', 'left'],
    ['name', 'left'],
    ['item', 'left'],
    ['quantity', 'right'],
    ['dailyRate', 'right'],
    ['exchangeRate', 'right'],
    ['amount', 'right'],
    ['dueDate', 'left'],
    ['expiryDate', 'left'],
    ['status', 'left'],
];

const contractCells = (contract: ClientSheetContract): string[] => [
    orDash(contract.NrContract),
    orDash(contract.Nume_client),
    orDash(contract.obiect_amanetat),
    orDash(contract.quantity),
    formatRate(contract.Comision_zi),
    formatRate(contract.Curs_BNR),
    formatRon(toNumber(contract.SumainLei)),
    formatDate(contract.Termen_restituire),
    formatDate(contract.Data_Exp),
    mapDatabaseStatusToDisplay(contract.Statut),
];

/** Build the sheet of a client from the contracts loaded for them, oldest contract first. */
export const buildClientSheet = (
    client: ClientSheetClient,
    contracts: readonly ClientSheetContract[],
    translate: ClientSheetTranslate,
): ClientSheet => {
    const label = (key: string): string => translate(`contracts.clientSheet.${key}`);
    const summary = summariseClientContracts(contracts);

    const field = (key: string, value: string): ClientSheetField => ({
        key,
        label: label(key),
        value,
    });

    return {
        clientName: client.Nume,
        details: [
            field('clientNumber', String(client.ClNr)),
            field('address', orDash(formatSheetAddress(client))),
            field('locality', orDash(client.Oras)),
            field('idSeries', orDash(client.SerieCI)),
            field('idNumber', orDash(client.NrCI)),
            field('category', orDash(client.Categorie)),
        ],
        observations: String(client.Notite ?? client.Observatii ?? '').trim(),
        columns: CONTRACT_COLUMNS.map(([key, align]) => ({ key, label: label(key), align })),
        rows: [...contracts]
            .sort((first, second) => first.Nr - second.Nr)
            .map(contract => ({ key: contract.Nr, cells: contractCells(contract) })),
        totals: [
            field('contractCount', String(summary.contractCount)),
            field('contractValue', formatRon(summary.contractValue)),
            field('expiredCount', String(summary.expiredCount)),
            field('expiredValue', formatRon(summary.expiredValue)),
            field('netProfit', formatRon(summary.netProfit)),
        ],
    };
};

/**
 * Semicolon-separated, because that is the list separator of the Romanian and Hungarian
 * Excel the shop opens it in; a comma-separated file lands in a single column there.
 */
const CSV_SEPARATOR = ';';

const escapeCsvValue = (value: string): string => `"${value.replace(/"/g, '""')}"`;

const csvLine = (values: readonly string[]): string =>
    values.map(escapeCsvValue).join(CSV_SEPARATOR);

/** The sheet as CSV: the client block, the contracts table, then the totals. */
export const buildClientSheetCsv = (sheet: ClientSheet, observationsLabel: string): string =>
    [
        csvLine([sheet.clientName]),
        ...sheet.details.map(detail => csvLine([detail.label, detail.value])),
        csvLine([observationsLabel, sheet.observations]),
        '',
        csvLine(sheet.columns.map(column => column.label)),
        ...sheet.rows.map(row => csvLine(row.cells)),
        '',
        ...sheet.totals.map(total => csvLine([total.label, total.value])),
    ].join('\r\n');

/** The download name of a sheet's CSV, e.g. `fisa-client-1234.csv`. */
export const clientSheetFileName = (client: ClientSheetClient): string =>
    `fisa-client-${client.ClNr}.csv`;
